import { useSearchAnalytics } from '../../pages/SearchPage/analytics'
import { Box } from '@moonpig/launchpad-components'
import React, { FC, useCallback, useMemo, useRef } from 'react'
import { Region } from '@moonpig/web-core-types'
import {
  AddedToBasketEvent,
  PageTypeEnum,
  ProductClickedEvent,
  ProductGrid,
  ProductTileProduct,
  PromoTileData,
} from '@moonpig/web-shared-products'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { LoadMoreType } from '../types'
import { InfiniteScroll } from './InfiniteScroll'
import { useLoadPage } from './useLoadPage'
import { setOffsetAndProductIdInUrl } from './setOffsetAndProductIdInUrl'
import { usePagingOffset } from './usePagingOffset'
import { useSearchStore } from '../../store/SearchStore'
import { AgeInputFilterBanner } from './EntryPointFeatureBanners/AgeInputFilterBanner'
import { PageNavigation } from './PageNavigation'

const setProductPosition = (
  product: ProductTileProduct,
  galleryPageSize: number,
) => {
  const productIndex = product.productIndex || 0
  const productPageNumber = Math.floor(productIndex / galleryPageSize)
  const productPageOffset = productPageNumber * galleryPageSize
  setOffsetAndProductIdInUrl(productPageOffset, product.id)
}

type ProductGridWithPagingProps = {
  totalCount: number
  products: ProductTileProduct[]
  onProductClicked: (clickedEvent: ProductClickedEvent) => void
  onProductFirstClick: (clickedEvent: ProductClickedEvent) => void
  onAddToBasket: (addToBasketEvent: AddedToBasketEvent) => void
  region: Region
  isInternalUser?: boolean
  metaTitle: string
  pageType: PageTypeEnum
  promoTile?: PromoTileData
}

export const ProductGridWithPaging: FC<ProductGridWithPagingProps> = ({
  totalCount,
  products,
  onProductClicked,
  onProductFirstClick,
  onAddToBasket,
  region,
  isInternalUser = false,
  metaTitle,
  pageType,
  promoTile,
}) => {
  const { departments } = useSearchStore(store => store.searchContext)
  const loadMoreProducts = useSearchStore(store => store.loadMoreProducts)
  const filters = useSearchStore(store => store.filters)
  const { inlineTickerMessages } = useSearchStore(store => store.pageContext)
  const searchCriteria = useSearchStore(store => store.searchCriteria)
  const topPage = useSearchStore(store => store.topPage)
  const bottomPage = useSearchStore(store => store.bottomPage)
  const galleryPageSize = useSearchStore(store => store.galleryPageSize)
  const isLaunchpadAnalytics = useSearchAnalytics()
  const multiBuyEnabled = useSearchStore(store => store.multiBuyEnabled)

  const productGridRef = useRef<HTMLDivElement>(null)

  const isIntegerAgeQuickFilterAvailable = filters.some(
    f =>
      f.__typename === 'IntegerFilter' &&
      f.integerId.toLowerCase() === 'age' &&
      f.isQuickFilter,
  )

  const isIntegerFilterApplied = filters.some(
    f => f.__typename === 'IntegerFilter' && f.isSelected,
  )

  const usePageNavigationExperiment =
    useExperiment('search-use-pagination', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const useAgeSelectionUx =
    useExperiment('search-use-age-selection-ux-v2', {
      fallback: 'control',
    })?.toLowerCase() === 'variant'

  const isFirstResultPage = topPage === 1 && bottomPage === 1
  const includeFirstPageGridModules =
    !usePageNavigationExperiment || isFirstResultPage

  const promoTileForGrid = includeFirstPageGridModules ? promoTile : undefined
  const inlineTickerMessagesForGrid = includeFirstPageGridModules
    ? inlineTickerMessages
    : undefined

  const shouldShowAgeInputFilterBanner =
    useAgeSelectionUx &&
    isIntegerAgeQuickFilterAvailable &&
    !isIntegerFilterApplied

  const featureEntryPointBannerForGrid =
    includeFirstPageGridModules && shouldShowAgeInputFilterBanner ? (
      /*istanbul ignore next*/ <AgeInputFilterBanner />
    ) : null

  const trackingData = useMemo(() => {
    return {
      pageType,
      metaTitle,
      region,
    }
  }, [pageType, metaTitle, region])

  const { loadCallback, loadState, loaded } = useLoadPage({
    loadMoreProducts,
    products,
  })

  const shouldInfiniteScrollUp = useRef(false)
  const shouldInfiniteScrollDown = useRef(false)

  const setShouldInfiniteScrollUp = (shouldInfiniteScroll: boolean) => {
    shouldInfiniteScrollUp.current = shouldInfiniteScroll
  }

  const setShouldInfiniteScrollDown = (shouldInfiniteScroll: boolean) => {
    shouldInfiniteScrollDown.current = shouldInfiniteScroll
  }

  const { offsetBorderApproaching } = usePagingOffset({
    loaded,
  })

  const endOfPageApproaching = useCallback(() => {
    if (shouldInfiniteScrollDown.current) {
      loadCallback(LoadMoreType.SCROLL)
    }
  }, [loadCallback, shouldInfiniteScrollDown])

  const topOfPageApproaching = useCallback(() => {
    if (shouldInfiniteScrollUp.current) {
      loadCallback(LoadMoreType.SCROLL_PREV)
    }
  }, [loadCallback, shouldInfiniteScrollUp])

  const handleProductClick = useCallback(
    (event: ProductClickedEvent) => {
      const { product } = event
      setProductPosition(product, galleryPageSize)
      onProductClicked(event)
    },
    [onProductClicked, galleryPageSize],
  )

  return (
    <Box>
      {usePageNavigationExperiment ? (
        <>
          <ProductGrid
            products={products}
            totalNumberOfProducts={totalCount}
            onProductFirstClick={onProductFirstClick}
            onProductClicked={handleProductClick}
            onAddToBasket={onAddToBasket}
            isLaunchpadAnalytics={isLaunchpadAnalytics}
            multiBuyEnabled={multiBuyEnabled}
            onEndApproaching={
              /* istanbul ignore next */
              () => {}
            }
            onTopApproaching={
              /* istanbul ignore next */
              () => {}
            }
            onOffsetBorderApproaching={
              /* istanbul ignore next */
              () => {}
            }
            isInternalUser={isInternalUser}
            departments={departments}
            trackingData={trackingData}
            promoTile={promoTileForGrid}
            featureEntryPointBanner={featureEntryPointBannerForGrid}
            searchCriteria={searchCriteria}
            inlineTickerMessages={inlineTickerMessagesForGrid}
            productGridRef={productGridRef}
          />
          <PageNavigation
            totalCount={totalCount}
            productGridRef={productGridRef}
          />
        </>
      ) : (
        <InfiniteScroll
          loaded={loaded}
          products={products}
          totalCount={totalCount}
          loadCallback={loadCallback}
          loadState={loadState}
          setShouldInfiniteScrollUp={setShouldInfiniteScrollUp}
          setShouldInfiniteScrollDown={setShouldInfiniteScrollDown}
        >
          <ProductGrid
            products={products}
            totalNumberOfProducts={totalCount}
            onProductFirstClick={onProductFirstClick}
            onProductClicked={handleProductClick}
            onAddToBasket={onAddToBasket}
            onEndApproaching={endOfPageApproaching}
            onTopApproaching={topOfPageApproaching}
            onOffsetBorderApproaching={offsetBorderApproaching}
            isInternalUser={isInternalUser}
            departments={departments}
            trackingData={trackingData}
            promoTile={promoTileForGrid}
            featureEntryPointBanner={featureEntryPointBannerForGrid}
            searchCriteria={searchCriteria}
            inlineTickerMessages={inlineTickerMessagesForGrid}
            isLaunchpadAnalytics={isLaunchpadAnalytics}
            multiBuyEnabled={multiBuyEnabled}
          />
        </InfiniteScroll>
      )}
    </Box>
  )
}
